import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase-config";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../assets/styles/AdminShared.css";

const initialFormData = {
  businessName: "",
  shortDescription: "",
  services: "",
  serviceArea: "",
  phone: "",
  email: "",
  businessHours: "",
  currentWebsite: "",
  socialLinks: "",
  preferredColors: "",
  referenceSites: "",
  mustHaveContent: "",
  avoid: "",
};

const sections = [
  {
    title: "Business Basics",
    fields: [
      ["businessName", "Business name", "input"],
      ["shortDescription", "What does the business do?", "textarea"],
      ["services", "Main services or products", "textarea"],
      ["serviceArea", "Service area", "input"],
    ],
  },
  {
    title: "Contact Details",
    fields: [
      ["phone", "Public phone number", "input"],
      ["email", "Public email", "input"],
      ["businessHours", "Business hours", "textarea"],
      ["currentWebsite", "Current website", "input"],
      ["socialLinks", "Social links", "textarea"],
    ],
  },
  {
    title: "Design Direction",
    fields: [
      ["preferredColors", "Preferred colors", "input"],
      ["referenceSites", "Reference websites", "textarea"],
      ["mustHaveContent", "Must-have content", "textarea"],
      ["avoid", "Things to avoid", "textarea"],
    ],
  },
];

const AdminStarterIntake = () => {
  const { projectId } = useParams();
  const [projectName, setProjectName] = useState("Loading...");
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const projectSnap = await getDoc(doc(db, "projects", projectId));
        if (!projectSnap.exists()) throw new Error("Project not found.");

        const data = projectSnap.data();
        setProjectName(data.projectName || "Unnamed Project");
        setFormData({ ...initialFormData, ...(data.starterIntake || {}) });
      } catch (err) {
        setError(err.message);
        toast.error("Failed to load Starter intake.");
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [projectId]);

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateDoc(doc(db, "projects", projectId), { starterIntake: formData });
      toast.success("Starter intake updated.");
      setError("");
    } catch (err) {
      setError("Failed to save intake: " + err.message);
      toast.error("Failed to save Starter intake.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="ap-loading"><p>Loading Starter intake...</p></div>;

  return (
    <div className="ap-page">
      <div className="ap-page-header">
        <a href={`/admin/project/${projectId}`} className="ap-back">← Back to project</a>
        <p className="ap-eyebrow">Admin — Starter</p>
        <h1 className="ap-title">{projectName}: Starter Intake</h1>
        <p className="ap-subtitle">View or edit the lightweight intake submitted for a Starter website.</p>
      </div>

      {error && <p className="ap-message--error" style={{ fontFamily: "var(--font-ui)", fontSize: "0.9375rem" }}>{error}</p>}

      {sections.map((section) => (
        <div key={section.title} className="ap-section" style={{ marginBottom: "20px" }}>
          <div className="ap-section-header">
            <h2 className="ap-section-title">{section.title}</h2>
          </div>
          <div className="ap-section-body">
            {section.fields.map(([name, label, type]) => (
              <div key={name} className="ap-form-group">
                <label>{label}</label>
                {type === "input" ? (
                  <input
                    type="text"
                    value={formData[name]}
                    onChange={(event) => handleChange(name, event.target.value)}
                  />
                ) : (
                  <textarea
                    value={formData[name]}
                    onChange={(event) => handleChange(name, event.target.value)}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="ap-btn-row">
        <button type="button" className="ap-btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
};

export default AdminStarterIntake;
